/** Journeys / paths page; all analytics are computed client-side from raw session data. */

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useMemo, useState } from "react";
import { ResponsiveContainer, Sankey, Tooltip } from "recharts";
import { SingleSegmentSelector } from "../components/segment-selector";
import { useDataStore, useFilters } from "../context";
import {
  DEFAULT_SEGMENTS,
  JourneyPath,
  PathTransition,
  Segment,
} from "../types/analytics";
import {
  adaptSessions,
  SessionWithDerivedFields,
} from "../utils/session-adapter";
import { filterSessionsBySegment } from "./overview/analytics";

// ============================================================================
// TYPES
// ============================================================================

type PathViewMode = "conversion" | "dropoff" | "transitions";

interface SankeyNode {
  name: string;
}

interface SankeyLink {
  source: number;
  target: number;
  value: number;
}

// ============================================================================
// PATH LIST COMPONENT
// ============================================================================

interface PathListProps {
  paths: JourneyPath[];
  title: string;
  loading?: boolean;
  emptyMessage: string;
}

const PathList: React.FC<PathListProps> = ({
  paths,
  title,
  loading,
  emptyMessage,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Stack spacing={1}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={50} />
          ))}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
        {title}
      </Typography>

      {paths.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          py={4}
        >
          {emptyMessage}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {paths.map((path, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                p: 1.5,
                "&:hover": {
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {path.path.map((page, i) => (
                    <React.Fragment key={i}>
                      <Chip
                        label={page}
                        size="small"
                        sx={{
                          maxWidth: 100,
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      />
                      {i < path.path.length - 1 && (
                        <ArrowForwardIcon fontSize="small" color="action" />
                      )}
                    </React.Fragment>
                  ))}
                </Stack>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  Sessions: <strong>{path.sessions.toLocaleString()}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Conv. Rate:{" "}
                  <strong
                    style={{
                      color:
                        path.conversionRate > 0.1
                          ? theme.palette.success.main
                          : "inherit",
                    }}
                  >
                    {(path.conversionRate * 100).toFixed(1)}%
                  </strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Duration:{" "}
                  <strong>{formatDuration(path.medianDuration)}</strong>
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

// ============================================================================
// TRANSITIONS TABLE COMPONENT
// ============================================================================

interface TransitionsTableProps {
  transitions: PathTransition[];
  loading?: boolean;
}

const TransitionsTable: React.FC<TransitionsTableProps> = ({
  transitions,
  loading,
}) => {
  if (loading) {
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell align="right">Sessions</TableCell>
              <TableCell align="right">%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton />
                </TableCell>
                <TableCell>
                  <Skeleton />
                </TableCell>
                <TableCell>
                  <Skeleton />
                </TableCell>
                <TableCell>
                  <Skeleton />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>From Page</TableCell>
            <TableCell>To Page</TableCell>
            <TableCell align="right">Sessions</TableCell>
            <TableCell align="right">Share</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transitions.map((t, i) => (
            <TableRow key={i} hover>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.from}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.to}
                </Typography>
              </TableCell>
              <TableCell align="right">{t.sessions.toLocaleString()}</TableCell>
              <TableCell align="right">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <Box sx={{ width: 50, mr: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={t.percentage * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                  {(t.percentage * 100).toFixed(1)}%
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {transitions.length === 0 && (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No transition data available
          </Typography>
        </Box>
      )}
    </TableContainer>
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatDuration(seconds: number): string {
  if (seconds >= 3600) {
    return `${(seconds / 3600).toFixed(1)}h`;
  }
  if (seconds >= 60) {
    return `${(seconds / 60).toFixed(1)}m`;
  }
  return `${Math.round(seconds)}s`;
}

function median(arr: number[]): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

function extractJourneysFromSessions(sessions: SessionWithDerivedFields[]): {
  conversionPaths: JourneyPath[];
  dropoffPaths: JourneyPath[];
  transitions: PathTransition[];
  sankeyData: { nodes: SankeyNode[]; links: SankeyLink[] };
} {
  if (sessions.length === 0) {
    return {
      conversionPaths: [],
      dropoffPaths: [],
      transitions: [],
      sankeyData: { nodes: [], links: [] },
    };
  }

  const totalSessions = sessions.length;

  // Build path frequency map (path string -> sessions with that path)
  const pathMap = new Map<string, SessionWithDerivedFields[]>();

  sessions.forEach((session) => {
    const pageFlow = session.pageFlow || [];
    if (pageFlow.length === 0) return;

    // Normalize path (take first 5 pages to avoid overly long paths)
    const normalizedPath = pageFlow.slice(0, 5).join(" → ");
    if (!pathMap.has(normalizedPath)) {
      pathMap.set(normalizedPath, []);
    }
    pathMap.get(normalizedPath)!.push(session);
  });

  // Build transition counts (excluding self-loops)
  const transitionCounts = new Map<string, number>();

  sessions.forEach((session) => {
    const pageFlow = session.pageFlow || [];
    for (let i = 0; i < pageFlow.length - 1; i++) {
      const from = pageFlow[i];
      const to = pageFlow[i + 1];
      // Skip self-loops
      if (from === to) continue;
      const key = `${from}|||${to}`;
      transitionCounts.set(key, (transitionCounts.get(key) || 0) + 1);
    }
  });

  // Convert to PathTransition array
  const transitions: PathTransition[] = Array.from(transitionCounts.entries())
    .map(([key, count]) => {
      const [from, to] = key.split("|||");
      return {
        from,
        to,
        sessions: count,
        percentage: totalSessions > 0 ? count / totalSessions : 0,
      };
    })
    .sort((a, b) => b.sessions - a.sessions)
    .slice(0, 20);

  // Sankey must be acyclic: depth = first appearance, then keep only low->high transitions.
  const nodeDepths = new Map<string, number>();
  nodeDepths.set("entry", 0);

  sessions.forEach((session) => {
    const pageFlow = session.pageFlow || [];
    pageFlow.forEach((page, index) => {
      // Use minimum depth seen for each page (first appearance)
      const currentDepth = nodeDepths.get(page);
      const newDepth = index + 1; // +1 because entry is 0
      if (currentDepth === undefined || newDepth < currentDepth) {
        nodeDepths.set(page, newDepth);
      }
    });
  });

  const maxDepth = Math.max(...Array.from(nodeDepths.values()), 0);
  nodeDepths.set("exit", maxDepth + 1);

  // Filter transitions to only keep forward-moving ones (lower depth -> higher depth)
  const acyclicTransitions = transitions.filter((t) => {
    const fromDepth = nodeDepths.get(t.from) ?? 0;
    const toDepth = nodeDepths.get(t.to) ?? 0;
    return fromDepth < toDepth;
  });

  const nodeSet = new Set<string>();
  nodeSet.add("entry");
  nodeSet.add("exit");
  acyclicTransitions.forEach((t) => {
    nodeSet.add(t.from);
    nodeSet.add(t.to);
  });

  const nodeArray = Array.from(nodeSet);
  const nodeIndexMap = new Map(nodeArray.map((n, i) => [n, i]));

  // Build sankey links including entry/exit
  const sankeyLinks: SankeyLink[] = [];

  const entryPageCounts = new Map<string, number>();
  sessions.forEach((session) => {
    const firstPage = session.pageFlow?.[0];
    if (firstPage) {
      entryPageCounts.set(firstPage, (entryPageCounts.get(firstPage) || 0) + 1);
    }
  });

  entryPageCounts.forEach((count, page) => {
    if (nodeIndexMap.has(page) && count >= 3) {
      sankeyLinks.push({
        source: nodeIndexMap.get("entry")!,
        target: nodeIndexMap.get(page)!,
        value: count,
      });
    }
  });

  acyclicTransitions.forEach((t) => {
    if (t.sessions >= 3 && nodeIndexMap.has(t.from) && nodeIndexMap.has(t.to)) {
      sankeyLinks.push({
        source: nodeIndexMap.get(t.from)!,
        target: nodeIndexMap.get(t.to)!,
        value: t.sessions,
      });
    }
  });

  const exitPageCounts = new Map<string, number>();
  sessions.forEach((session) => {
    const pageFlow = session.pageFlow || [];
    const lastPage = pageFlow[pageFlow.length - 1];
    if (lastPage) {
      exitPageCounts.set(lastPage, (exitPageCounts.get(lastPage) || 0) + 1);
    }
  });

  exitPageCounts.forEach((count, page) => {
    if (nodeIndexMap.has(page) && count >= 3) {
      sankeyLinks.push({
        source: nodeIndexMap.get(page)!,
        target: nodeIndexMap.get("exit")!,
        value: count,
      });
    }
  });

  const sankeyData = {
    nodes: nodeArray.map((name) => ({ name })),
    links: sankeyLinks,
  };

  // Build conversion paths (paths that led to signUp or contact)
  const conversionPaths: JourneyPath[] = [];
  const dropoffPaths: JourneyPath[] = [];

  pathMap.forEach((pathSessions, pathString) => {
    if (pathSessions.length < 2) return; // Skip very rare paths

    const path = pathString.split(" → ");
    const convertedSessions = pathSessions.filter(
      (s) => s.signedUp || s.contacted
    );
    const conversionRate = convertedSessions.length / pathSessions.length;
    const durations = pathSessions.map((s) => s.sessionDuration || 0);
    const medianDuration = median(durations);

    const journeyPath: JourneyPath = {
      path,
      sessions: pathSessions.length,
      conversionRate,
      medianDuration,
    };

    if (conversionRate > 0) {
      conversionPaths.push(journeyPath);
    } else {
      dropoffPaths.push(journeyPath);
    }
  });

  // Sort and limit paths
  conversionPaths.sort((a, b) => {
    if (Math.abs(b.conversionRate - a.conversionRate) > 0.01) {
      return b.conversionRate - a.conversionRate;
    }
    return b.sessions - a.sessions;
  });

  dropoffPaths.sort((a, b) => b.sessions - a.sessions);

  return {
    conversionPaths: conversionPaths.slice(0, 10),
    dropoffPaths: dropoffPaths.slice(0, 10),
    transitions,
    sankeyData,
  };
}

// ============================================================================
// MAIN JOURNEYS PAGE COMPONENT
// ============================================================================

export const JourneysPage: React.FC = () => {
  const { savedSegments } = useFilters();
  const {
    sessions,
    sessionsLoading: loading,
    sessionsError: error,
  } = useDataStore();
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<PathViewMode>("conversion");

  // Local state for segment filter
  const [activeSegment, setActiveSegment] = useState<Segment>(
    savedSegments[0] || DEFAULT_SEGMENTS[0]
  );

  const filteredSessions = useMemo(() => {
    const segmentFiltered = filterSessionsBySegment(sessions, activeSegment);
    return adaptSessions(segmentFiltered);
  }, [sessions, activeSegment]);

  const journeysData = useMemo(
    () => extractJourneysFromSessions(filteredSessions),
    [filteredSessions]
  );

  const handleViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: PathViewMode | null
  ) => {
    if (newView) {
      setViewMode(newView);
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Paper sx={{ mb: 3, p: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                User Journeys
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Understand how users navigate through your site and what paths
                lead to conversion.
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              size="small"
            >
              <ToggleButton value="conversion">Conversion Paths</ToggleButton>
              <ToggleButton value="dropoff">Drop-off Paths</ToggleButton>
              <ToggleButton value="transitions">Transitions</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Segment Selector */}
          <SingleSegmentSelector
            selectedSegment={activeSegment}
            onSegmentChange={setActiveSegment}
          />
        </Stack>
      </Paper>

      {viewMode === "conversion" && (
        <PathList
          paths={journeysData.conversionPaths}
          title="Top Paths to Conversion"
          loading={loading}
          emptyMessage="No conversion path data available"
        />
      )}

      {viewMode === "dropoff" && (
        <PathList
          paths={journeysData.dropoffPaths}
          title="Top Drop-off Paths"
          loading={loading}
          emptyMessage="No drop-off path data available"
        />
      )}

      {viewMode === "transitions" && (
        <Grid container spacing={3}>
          <Grid size={12}>
            <TransitionsTable
              transitions={journeysData.transitions}
              loading={loading}
            />
          </Grid>
        </Grid>
      )}

      {journeysData.sankeyData.nodes.length > 0 && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Page Flow Visualization
          </Typography>
          <Box sx={{ height: 400 }}>
            {loading ? (
              <Skeleton variant="rectangular" height={380} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <Sankey
                  data={journeysData.sankeyData}
                  node={{
                    fill: theme.palette.primary.main,
                    fillOpacity: 0.8,
                  }}
                  link={{
                    stroke: theme.palette.grey[300],
                  }}
                  nodePadding={30}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <Tooltip />
                </Sankey>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};
